// Document Viewer Panel Component (Max 3 Tabs - FIFO)
import React, { useState, useEffect } from 'react';
import { FileText, Loader2, Plus, X } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const DocumentViewerPanel = ({ meetingId, agendaItems, isOpen, onClose }) => {
  const [linkedDocs, setLinkedDocs] = useState([]);
  const [availableDocs, setAvailableDocs] = useState([]);
  const [openTabs, setOpenTabs] = useState([]); // Max 3 documents - FIFO queue
  const [activeTabId, setActiveTabId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [selectedDocToAdd, setSelectedDocToAdd] = useState('');
  const MAX_TABS = 3;

  useEffect(() => {
    if (isOpen && meetingId) {
      fetchDocuments();
    }
  }, [isOpen, meetingId]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/board/meetings/${meetingId}/documents`);
      setLinkedDocs(res.data.linked_documents || []);
      setAvailableDocs(res.data.available_documents || []);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const openDocument = (doc) => {
    // Check if already open
    if (openTabs.find(t => t.document_id === doc.document_id)) {
      setActiveTabId(doc.document_id);
      return;
    }
    
    // FIFO: If we have MAX_TABS, remove the oldest one (first in queue)
    let newTabs = [...openTabs];
    if (newTabs.length >= MAX_TABS) {
      newTabs = newTabs.slice(1); // Remove first (oldest) document
    }
    
    // Add new document to the end
    newTabs.push(doc);
    setOpenTabs(newTabs);
    setActiveTabId(doc.document_id);
  };

  const closeTab = (docId, e) => {
    e?.stopPropagation();
    const newTabs = openTabs.filter(t => t.document_id !== docId);
    setOpenTabs(newTabs);
    
    // If we closed the active tab, activate the last remaining one
    if (activeTabId === docId && newTabs.length > 0) {
      setActiveTabId(newTabs[newTabs.length - 1].document_id);
    } else if (newTabs.length === 0) {
      setActiveTabId(null);
    }
  };

  const linkDocument = async () => {
    if (!selectedDocToAdd) return;
    
    try {
      await api.post(`/board/meetings/${meetingId}/documents`, {
        document_id: selectedDocToAdd
      });
      toast.success('Document linked to meeting');
      setSelectedDocToAdd('');
      setShowAddDoc(false);
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link document');
    }
  };

  const unlinkDocument = async (docId) => {
    if (!window.confirm('Remove this document from the meeting?')) return;
    
    try {
      await api.delete(`/board/meetings/${meetingId}/documents/${docId}`);
      toast.success('Document removed');
      // Also close the tab if open
      closeTab(docId);
      fetchDocuments();
    } catch (err) {
      toast.error('Failed to remove document');
    }
  };

  const activeDoc = openTabs.find(t => t.document_id === activeTabId);
  
  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return '📄';
    if (fileType?.includes('word') || fileType?.includes('doc')) return '📝';
    if (fileType?.includes('excel') || fileType?.includes('sheet')) return '📊';
    if (fileType?.includes('image')) return '🖼️';
    return '📎';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="p-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-600" />
            Meeting Documents
            <span className="text-sm font-normal text-gray-500 ml-2">
              (Max {MAX_TABS} open at once)
            </span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
          </div>
        ) : (
          <div className="flex flex-1 overflow-hidden">
            {/* Left Sidebar - Document List */}
            <div className="w-64 border-r bg-gray-50 flex flex-col">
              <div className="p-3 border-b bg-white">
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setShowAddDoc(!showAddDoc)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Document
                </Button>
              </div>
              
              {/* Add Document Form */}
              {showAddDoc && (
                <div className="p-3 border-b bg-purple-50 space-y-2">
                  <Select value={selectedDocToAdd} onValueChange={setSelectedDocToAdd}>
                    <SelectTrigger className="text-sm">
                      <SelectValue placeholder="Select document" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableDocs
                        .filter(d => !linkedDocs.find(l => l.document_id === d.id))
                        .map(doc => (
                          <SelectItem key={doc.id} value={doc.id}>
                            {getFileIcon(doc.file_type)} {doc.title}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" className="w-full" onClick={linkDocument} disabled={!selectedDocToAdd}>
                    Link to Meeting
                  </Button>
                </div>
              )}
              
              {/* Linked Documents List */}
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {linkedDocs.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-4">No documents linked</p>
                ) : (
                  linkedDocs.map(doc => (
                    <div
                      key={doc.document_id}
                      className={`p-2 rounded-lg cursor-pointer transition-colors group ${
                        openTabs.find(t => t.document_id === doc.document_id)
                          ? 'bg-purple-100 border border-purple-300'
                          : 'hover:bg-gray-100'
                      }`}
                      onClick={() => openDocument(doc)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <span>{getFileIcon(doc.file_type)}</span>
                          <span className="text-sm font-medium truncate">{doc.title}</span>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); unlinkDocument(doc.document_id); }}
                          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 p-1"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                      {doc.agenda_item_title && (
                        <p className="text-xs text-gray-500 mt-1 truncate">
                          📋 {doc.agenda_item_title}
                        </p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Main Content - Document Viewer */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Tabs */}
              {openTabs.length > 0 && (
                <div className="flex border-b bg-gray-100 overflow-x-auto">
                  {openTabs.map((doc, index) => (
                    <div
                      key={doc.document_id}
                      onClick={() => setActiveTabId(doc.document_id)}
                      className={`flex items-center gap-2 px-4 py-2 border-r cursor-pointer min-w-0 max-w-[200px] ${
                        activeTabId === doc.document_id
                          ? 'bg-white border-b-2 border-b-purple-600'
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-xs text-gray-400">{index + 1}</span>
                      <span className="text-sm truncate flex-1">{doc.title}</span>
                      <button
                        onClick={(e) => closeTab(doc.document_id, e)}
                        className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Document Content */}
              <div className="flex-1 overflow-auto bg-gray-200">
                {activeDoc ? (
                  <div className="h-full flex flex-col">
                    {/* Document Info Bar */}
                    <div className="bg-white border-b p-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{activeDoc.title}</span>
                        <Badge variant="outline" className="text-xs">{activeDoc.file_type}</Badge>
                      </div>
                      <a 
                        href={activeDoc.file_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-purple-600 hover:underline"
                      >
                        Open in new tab ↗
                      </a>
                    </div>
                    
                    {/* PDF/Document Preview */}
                    <div className="flex-1 bg-gray-800">
                      {activeDoc.file_type?.includes('pdf') || activeDoc.preview_url ? (
                        <iframe
                          src={activeDoc.preview_url || activeDoc.file_url}
                          className="w-full h-full"
                          title={activeDoc.title}
                        />
                      ) : activeDoc.file_type?.includes('image') ? (
                        <div className="flex items-center justify-center h-full p-4">
                          <img 
                            src={activeDoc.file_url} 
                            alt={activeDoc.title}
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full text-white">
                          <FileText className="h-16 w-16 mb-4 opacity-50" />
                          <p className="text-lg mb-2">Preview not available</p>
                          <a 
                            href={activeDoc.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-700"
                          >
                            Download Document
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500">
                    <FileText className="h-16 w-16 mb-4 opacity-30" />
                    <p className="text-lg">Select a document to view</p>
                    <p className="text-sm mt-1">Click on a document from the list</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentViewerPanel;
